import ExcelJS from 'exceljs'
import moment from 'moment-timezone'
import { IDailySalesExportData, ICustomerSalesData, PaymentMethod } from '@/supabase/DailySalesSummary/dailySalesSummary.interface'

export class ExcelExportService {
  private workbook: ExcelJS.Workbook
  private worksheet: ExcelJS.Worksheet

  constructor() {
    this.workbook = new ExcelJS.Workbook()
    this.worksheet = this.workbook.addWorksheet('สรุปการขาย')
  }

  // Format currency for display
  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount)
  }

  // Format quantity for display
  private formatQuantity(quantity: number): string {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(quantity)
  }

  // Get payment method label for a single entry
  private getPaymentMethodLabel(method: PaymentMethod): string {
    if (method === PaymentMethod.CASH) return 'สด'
    if (method === PaymentMethod.TRANSFER) return 'โอน'
    return ''
  }

  // Set cell style
  private setCellStyle(cell: ExcelJS.Cell, options: {
    bold?: boolean
    fontSize?: number
    backgroundColor?: string
    fontColor?: string
    border?: boolean
    alignment?: 'left' | 'center' | 'right'
  }) {
    if (options.bold) {
      cell.font = { ...cell.font, bold: true }
    }
    if (options.fontSize) {
      cell.font = { ...cell.font, size: options.fontSize }
    }
    if (options.fontColor) {
      cell.font = { ...cell.font, color: { argb: options.fontColor } }
    }
    if (options.backgroundColor) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: options.backgroundColor }
      }
    }
    if (options.border) {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }
    }
    if (options.alignment) {
      cell.alignment = { horizontal: options.alignment, vertical: 'middle' }
    }
  }

  // Add header section
  private addHeader(data: IDailySalesExportData) {
    const dateFormatted = moment(data.date, 'YYYY-MM-DD').format('DD/MM/YYYY')
    
    // Title
    const titleCell = this.worksheet.getCell('A1')
    titleCell.value = `สรุปการขาย - ${dateFormatted}`
    this.setCellStyle(titleCell, {
      bold: true,
      fontSize: 14,
      alignment: 'center'
    })
    
    // Merge title cells
    this.worksheet.mergeCells('A1:K1')
  }

  // Add customer data table
  private addCustomerTable(customers: ICustomerSalesData[], startRow: number): number {
    let currentRow = startRow

    // First row - merge cells for grouped headers
    // ชื่อลูกค้า (merge 2 rows)
    const customerNameCell = this.worksheet.getCell(currentRow, 1)
    customerNameCell.value = 'ชื่อลูกค้า'
    this.setCellStyle(customerNameCell, {
      bold: true,
      backgroundColor: 'FFE6E6E6',
      border: true,
      alignment: 'center'
    })
    this.worksheet.mergeCells(currentRow, 1, currentRow + 1, 1)

    // ขาย header spanning over product columns
    const saleHeaderCell = this.worksheet.getCell(currentRow, 2)
    saleHeaderCell.value = 'ขาย'
    this.setCellStyle(saleHeaderCell, {
      bold: true,
      backgroundColor: 'FFE6E6E6',
      border: true,
      alignment: 'center'
    })
    this.worksheet.mergeCells(currentRow, 2, currentRow, 5) // Merge columns B to E (ใหญ่, เล็ก, โม่, รวม)
    
    // Other grouped headers (merge 2 rows each)
    const groupedHeaders = [
      { col: 6, text: 'จำนวนเงิน' },
      { col: 7, text: 'จ่ายแล้ว' },
      { col: 8, text: 'ช่องทาง' },
      { col: 9, text: 'ค้างจ่าย' },
      { col: 10, text: 'หมายเหตุ' },
      { col: 11, text: 'ยกเลิกรายการ' }
    ]

    groupedHeaders.forEach(({ col, text }) => {
      const cell = this.worksheet.getCell(currentRow, col)
      cell.value = text
      this.setCellStyle(cell, {
        bold: true,
        backgroundColor: 'FFE6E6E6',
        border: true,
        alignment: 'center'
      })
      this.worksheet.mergeCells(currentRow, col, currentRow + 1, col)
    })

    currentRow++

    // Second row - only product detail headers (ชื่อลูกค้า and others are merged from above)
    const productHeaders = ['ใหญ่', 'เล็ก', 'โม่', 'รวม']
    productHeaders.forEach((header, index) => {
      const cell = this.worksheet.getCell(currentRow, index + 2) // Start from column 2
      cell.value = header
      this.setCellStyle(cell, {
        bold: true,
        backgroundColor: 'FFE6E6E6',
        border: true,
        alignment: 'center'
      })
    })

    currentRow++

    // Separate customers by type (no grouping — each customer = 1 order = 1 row)
    const regularCustomers = customers.filter(c => c.customerType === 'REGULAR')
    const generalCustomers = customers.filter(c => c.customerType === 'GENERAL')

    // Add regular customers
    if (regularCustomers.length > 0) {
      // Regular customers header
      const regularHeaderCell = this.worksheet.getCell(currentRow, 1)
      regularHeaderCell.value = 'ลูกค้าประจำ'
      this.setCellStyle(regularHeaderCell, {
        bold: true,
        backgroundColor: 'AAC4F5',
        border: true
      })
      this.worksheet.mergeCells(currentRow, 1, currentRow, 11)
      currentRow++

      // Add regular customer data — one row per payment entry
      regularCustomers.forEach(customer => {
        const entries = customer.paymentEntries || []
        const rowCount = Math.max(entries.length, 1) // At least 1 row per customer
        const paidFromEntries = entries.reduce((sum, e) => sum + e.amountTHB, 0)
        const outstanding = Math.max(0, customer.totalAmountTHB - paidFromEntries)

        for (let i = 0; i < rowCount; i++) {
          const entry = entries[i]
          const isFirstRow = i === 0

          const rowData = [
            isFirstRow ? customer.customerName : '',
            isFirstRow ? this.formatQuantity(customer.largeIceCubeAmount) : '',
            isFirstRow ? this.formatQuantity(customer.smallIceCubeAmount) : '',
            isFirstRow ? this.formatQuantity(customer.crushedIceAmount + customer.coarselyCrushedIceAmount) : '',
            isFirstRow ? this.formatQuantity(customer.totalQuantity) : '',
            isFirstRow ? this.formatCurrency(customer.totalAmountTHB) : '',
            entry ? this.formatCurrency(entry.amountTHB) : '',
            entry ? this.getPaymentMethodLabel(entry.paymentMethod) : '',
            isFirstRow ? this.formatCurrency(outstanding) : '',
            isFirstRow ? (entry?.note || '') : '',
            isFirstRow ? (customer.isVoidedFromSales ? 'X' : '') : ''
          ]

          rowData.forEach((value, index) => {
            const cell = this.worksheet.getCell(currentRow, index + 1)
            cell.value = value
            this.setCellStyle(cell, {
              border: true,
              alignment: index === 0 ? 'left' : index === 10 ? 'center' : 'right'
            })
          })
          currentRow++
        }
      })
    }

    // Add general customers
    if (generalCustomers.length > 0) {
      // General customers header
      const generalHeaderCell = this.worksheet.getCell(currentRow, 1)
      generalHeaderCell.value = 'ลูกค้าทั่วไป'
      this.setCellStyle(generalHeaderCell, {
        bold: true,
        backgroundColor: 'AAC4F5',
        border: true
      })
      this.worksheet.mergeCells(currentRow, 1, currentRow, 11)
      currentRow++

      // Add general customer data — one row per payment entry
      generalCustomers.forEach(customer => {
        const entries = customer.paymentEntries || []
        const rowCount = Math.max(entries.length, 1)
        const paidFromEntries = entries.reduce((sum, e) => sum + e.amountTHB, 0)
        const outstanding = Math.max(0, customer.totalAmountTHB - paidFromEntries)

        for (let i = 0; i < rowCount; i++) {
          const entry = entries[i]
          const isFirstRow = i === 0

          const rowData = [
            isFirstRow ? customer.customerName : '',
            isFirstRow ? this.formatQuantity(customer.largeIceCubeAmount) : '',
            isFirstRow ? this.formatQuantity(customer.smallIceCubeAmount) : '',
            isFirstRow ? this.formatQuantity(customer.crushedIceAmount + customer.coarselyCrushedIceAmount) : '',
            isFirstRow ? this.formatQuantity(customer.totalQuantity) : '',
            isFirstRow ? this.formatCurrency(customer.totalAmountTHB) : '',
            entry ? this.formatCurrency(entry.amountTHB) : '',
            entry ? this.getPaymentMethodLabel(entry.paymentMethod) : '',
            isFirstRow ? this.formatCurrency(outstanding) : '',
            isFirstRow ? (entry?.note || '') : '',
            isFirstRow ? (customer.isVoidedFromSales ? 'X' : '') : ''
          ]

          rowData.forEach((value, index) => {
            const cell = this.worksheet.getCell(currentRow, index + 1)
            cell.value = value
            this.setCellStyle(cell, {
              border: true,
              alignment: index === 0 ? 'left' : index === 10 ? 'center' : 'right'
            })
          })
          currentRow++
        }
      })
    }

    // Add totals row (filter out voided orders)
    const activeCustomers = customers.filter(c => !c.isVoidedFromSales)
    const totals = activeCustomers.reduce(
      (acc, customer) => {
        const paidFromEntries = (customer.paymentEntries || []).reduce((sum, e) => sum + e.amountTHB, 0)
        return {
          largeTotal: acc.largeTotal + customer.largeIceCubeAmount,
          smallTotal: acc.smallTotal + customer.smallIceCubeAmount,
          crushedTotal: acc.crushedTotal + customer.crushedIceAmount + customer.coarselyCrushedIceAmount,
          totalQuantity: acc.totalQuantity + customer.totalQuantity,
          totalAmount: acc.totalAmount + customer.totalAmountTHB,
          totalPaid: acc.totalPaid + paidFromEntries,
          totalOutstanding: acc.totalOutstanding + Math.max(0, customer.totalAmountTHB - paidFromEntries),
        }
      },
      { 
        largeTotal: 0, 
        smallTotal: 0, 
        crushedTotal: 0, 
        totalQuantity: 0, 
        totalAmount: 0, 
        totalPaid: 0, 
        totalOutstanding: 0,
      }
    )

    const totalRowData = [
      'รวม',
      this.formatQuantity(totals.largeTotal),
      this.formatQuantity(totals.smallTotal),
      this.formatQuantity(totals.crushedTotal),
      this.formatQuantity(totals.totalQuantity),
      this.formatCurrency(totals.totalAmount),
      this.formatCurrency(totals.totalPaid),
      '',
      this.formatCurrency(totals.totalOutstanding),
      '',
      ''
    ]

    totalRowData.forEach((value, index) => {
      const cell = this.worksheet.getCell(currentRow, index + 1)
      cell.value = value
      this.setCellStyle(cell, {
        bold: true,
        backgroundColor: 'FFFFCC99',
        border: true,
        alignment: index === 0 ? 'left' : 'right'
      })
    })

    currentRow++

    // Add "รวมขาย X กระสอบ" row (filter out voided orders)
    const totalBags = activeCustomers.reduce((sum, customer) => 
      sum + customer.largeIceCubeAmount + customer.smallIceCubeAmount + 
      customer.crushedIceAmount + (customer.coarselyCrushedIceAmount || 0), 0
    )

    const totalBagsCell = this.worksheet.getCell(currentRow, 1)
    totalBagsCell.value = `รวมขาย ${this.formatQuantity(totalBags)} กระสอบ`
    this.setCellStyle(totalBagsCell, {
      bold: true,
      backgroundColor: 'FFDDDDDD',
      border: true,
      fontSize: 12,
      alignment: 'left'
    })
    this.worksheet.mergeCells(currentRow, 1, currentRow, 11)

    return currentRow + 2 // Return next available row with spacing
  }

  // Add payment summary
  private addPaymentSummary(data: IDailySalesExportData, startRow: number): number {
    let currentRow = startRow

    // Payment summary title
    const titleCell = this.worksheet.getCell(currentRow, 1)
    titleCell.value = 'สรุปการชำระเงิน'
    this.setCellStyle(titleCell, {
      bold: true,
      fontSize: 14,
      backgroundColor: 'AAC4F5',
      border: true
    })
    this.worksheet.mergeCells(currentRow, 1, currentRow, 6)
    currentRow++

    // Payment details - group label and value together
    const paymentData = [
      ['จ่ายแล้ว - สด', this.formatCurrency(data.paymentSummary.totalCashPayments)],
      ['จ่ายแล้ว - โอน', this.formatCurrency(data.paymentSummary.totalTransferPayments)],
      ['เหลือเงินสด', this.formatCurrency(data.paymentSummary.remainingCashTHB)]
    ]

    paymentData.forEach(([label, value]) => {
      // Merge label across columns 1-5 for more space
      const labelCell = this.worksheet.getCell(currentRow, 1)
      labelCell.value = label
      this.setCellStyle(labelCell, { 
        border: true, 
        alignment: 'left',
        backgroundColor: 'FFFFFFFF'
      })
      this.worksheet.mergeCells(currentRow, 1, currentRow, 5)
      
      // Value in column 6
      const valueCell = this.worksheet.getCell(currentRow, 6)
      valueCell.value = value
      this.setCellStyle(valueCell, { 
        border: true, 
        alignment: 'right',
        backgroundColor: 'FFFFFFFF'
      })
      
      currentRow++
    })

    return currentRow + 1
  }

  // Add expenses section
  private addExpenses(data: IDailySalesExportData, startRow: number): number {
    let currentRow = startRow

    // Expenses title
    const titleCell = this.worksheet.getCell(currentRow, 1)
    titleCell.value = 'ค่าใช้จ่าย'
    this.setCellStyle(titleCell, {
      bold: true,
      fontSize: 14,
      backgroundColor: 'AAC4F5',
      border: true
    })
    this.worksheet.mergeCells(currentRow, 1, currentRow, 6)
    currentRow++

    // Add each expense type (always show all 3 types)
    const expenseTypes = [
      { key: 'FUEL' as const, name: 'เติมน้ำมัน' },
      { key: 'EMPLOYEE_OVERTIME' as const, name: 'OT' },
      { key: 'OTHER' as const, name: 'อื่นๆ' }
    ]

    expenseTypes.forEach(({ key, name }) => {
      const expenses = data.expenses[key] || []
      
      // Expense type header (always show)
      const typeHeaderCell = this.worksheet.getCell(currentRow, 1)
      typeHeaderCell.value = name
      this.setCellStyle(typeHeaderCell, {
        bold: true,
        backgroundColor: 'FFEEEEEE',
        border: true
      })
      this.worksheet.mergeCells(currentRow, 1, currentRow, 6)
      currentRow++

      // Ensure minimum 3 rows per expense type
      const minRows = 3
      let rowsUsed = 0

      // Add expense items if any
      if (expenses.length > 0) {
        expenses.forEach(expense => {
          // Merge name across columns 1-5 for more space
          const nameCell = this.worksheet.getCell(currentRow, 1)
          nameCell.value = expense.name
          this.setCellStyle(nameCell, { 
            border: true, 
            alignment: 'left',
            backgroundColor: 'FFFFFFFF'
          })
          this.worksheet.mergeCells(currentRow, 1, currentRow, 5)
          
          // Amount in column 6
          const amountCell = this.worksheet.getCell(currentRow, 6)
          amountCell.value = this.formatCurrency(expense.amountTHB)
          this.setCellStyle(amountCell, { 
            border: true, 
            alignment: 'right',
            backgroundColor: 'FFFFFFFF'
          })
          
          currentRow++
          rowsUsed++
        })
      }

      // Add empty rows to reach minimum
      while (rowsUsed < minRows) {
        // Empty name cell merged across columns 1-5
        const nameCell = this.worksheet.getCell(currentRow, 1)
        nameCell.value = ''
        this.setCellStyle(nameCell, { 
          border: true, 
          alignment: 'left',
          backgroundColor: 'FFFFFFFF'
        })
        this.worksheet.mergeCells(currentRow, 1, currentRow, 5)
        
        // Empty amount cell in column 6
        const amountCell = this.worksheet.getCell(currentRow, 6)
        amountCell.value = ''
        this.setCellStyle(amountCell, { 
          border: true, 
          alignment: 'right',
          backgroundColor: 'FFFFFFFF'
        })
        
        currentRow++
        rowsUsed++
      }

      // Expense type total
      const total = expenses.reduce((sum, expense) => sum + expense.amountTHB, 0)
      
      // Total label merged across columns 1-5
      const totalLabelCell = this.worksheet.getCell(currentRow, 1)
      totalLabelCell.value = `รวม ${name}`
      this.setCellStyle(totalLabelCell, { 
        bold: true, 
        border: true, 
        backgroundColor: 'FFDDDDDD',
        alignment: 'left' 
      })
      this.worksheet.mergeCells(currentRow, 1, currentRow, 5)
      
      // Total amount in column 6
      const totalAmountCell = this.worksheet.getCell(currentRow, 6)
      totalAmountCell.value = this.formatCurrency(total)
      this.setCellStyle(totalAmountCell, { 
        bold: true, 
        border: true, 
        backgroundColor: 'FFFFCC99',
        alignment: 'right' 
      })
      
      currentRow += 2 // Add spacing between expense types
    })

    return currentRow
  }

  // Set column widths
  private setColumnWidths() {
    const columnWidths = [
      { column: 1, width: 15 }, // ชื่อลูกค้า
      { column: 2, width: 5 }, // ใหญ่
      { column: 3, width: 5 }, // เล็ก
      { column: 4, width: 5 }, // โม่
      { column: 5, width: 5 }, // รวม
      { column: 6, width: 15 }, // จำนวนเงิน
      { column: 7, width: 15 }, // จ่ายแล้ว
      { column: 8, width: 12 }, // ช่องทาง
      { column: 9, width: 15 }, // ค้างจ่าย
      { column: 10, width: 20 }, // หมายเหตุ
      { column: 11, width: 14 }  // ยกเลิกรายการ
    ]

    columnWidths.forEach(({ column, width }) => {
      this.worksheet.getColumn(column).width = width
    })
  }

  // Generate and download Excel file
  async generateAndDownload(data: IDailySalesExportData): Promise<void> {
    try {
      // Create a fresh workbook and worksheet for each export to avoid merge conflicts
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('สรุปการขาย')
      
      // Temporarily assign the new worksheet to use existing methods
      const originalWorksheet = this.worksheet
      this.worksheet = worksheet

      let currentRow = 1

      // Add header
      this.addHeader(data)
      currentRow = 4

      // Add customer table
      currentRow = this.addCustomerTable(data.customers, currentRow)

      // Add payment summary
      currentRow = this.addPaymentSummary(data, currentRow)

      // Add expenses
      this.addExpenses(data, currentRow)

      // Set column widths
      this.setColumnWidths()

      // Restore original worksheet
      this.worksheet = originalWorksheet

      // Generate buffer
      const buffer = await workbook.xlsx.writeBuffer()

      // Create filename
      const dateFormatted = moment(data.date, 'YYYY-MM-DD').format('DD-MM-YYYY')
      const filename = `sk_unic_สรุปการขาย_${dateFormatted}.xlsx`

      // Download file using native browser API
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      
      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

    } catch (error) {
      console.error('Error generating Excel file:', error)
      throw new Error('เกิดข้อผิดพลาดในการสร้างไฟล์ Excel')
    }
  }
}

// Export singleton instance
export const excelExportService = new ExcelExportService()